<script setup lang="ts">
import { useI18n } from '@/i18n';
import type { Subscription } from '@/types/billing';
import { Badge, Icon } from '@dabble/quillui';
import { formatDistanceToNow } from 'date-fns';
import { computed } from 'vue';

const props = defineProps<{ subscription?: Subscription | null }>();

// Composables
const { t } = useI18n();

// Resolve the pause-resume timestamp (Stripe seconds). Prefer Stripe's native
// pause_collection.resumes_at; fall back to the legacy homebrew marker on
// metadata.is_paused_until (or trial_end, which the old system reused).
function resolvePauseResumeSeconds(sub: Subscription): number | null {
  const native = sub.pause_collection?.resumes_at;
  if (typeof native === 'number') return native;
  const legacyMeta = sub.metadata?.is_paused_until;
  if (typeof legacyMeta === 'number') return legacyMeta;
  if (typeof legacyMeta === 'string' && legacyMeta) {
    const parsed = Number(legacyMeta);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return sub.trial_end ?? null;
}

function isSubscriptionPaused(sub: Subscription): boolean {
  if (sub.status === 'paused') return true;
  if (sub.pause_collection?.behavior) return true;
  if (sub.metadata?.is_paused_until) return true;
  return false;
}

type BadgeVariant = 'neutral' | 'warning' | 'danger' | 'success' | 'info';

const badgeInfo = computed<{ text: string; variant: BadgeVariant; icon: string | null }>(() => {
  const sub = props.subscription;
  let text = '';
  let variant: BadgeVariant = 'neutral';
  let icon: string | null = null;

  if (!sub || sub.status === 'canceled' || sub.status === 'incomplete_expired' || sub.status === 'unpaid') {
    text = t('billing_plan_no_subscription', 'Inactive');
    variant = 'neutral';
  } else if (isSubscriptionPaused(sub)) {
    const resumeSeconds = resolvePauseResumeSeconds(sub);
    const dateStr = resumeSeconds
      ? formatDistanceToNow(new Date(resumeSeconds * 1000), { addSuffix: true })
      : 'an unknown date';
    text = t('billing_plan_paused_resumes', { date: dateStr });
    variant = 'warning';
  } else if (sub.status === 'trialing') {
    const dateStr = sub.trial_end
      ? formatDistanceToNow(new Date(sub.trial_end * 1000), { addSuffix: true })
      : 'an unknown date';
    const planName = sub.plan?.name || sub.plan?.product?.name;
    text = planName
      ? t('billing_plan_trialing_with_plan', { plan: planName, date: dateStr })
      : t('billing_plan_trial_ends', { date: dateStr });
    variant = 'warning';
    icon = 'clock';
  } else if (sub.cancel_at_period_end) {
    text = t('billing_plan_cancels');
    variant = 'warning';
    icon = 'clock';
  } else if (sub.status === 'past_due') {
    text = t('billing_plan_past_due', 'Past Due');
    variant = 'danger';
  } else if (sub.status === 'incomplete') {
    text = t('billing_plan_incomplete', 'Incomplete');
    variant = 'warning';
  }

  return { text, variant, icon };
});
</script>

<template>
  <template v-if="badgeInfo.text">
    <Badge :variant="badgeInfo.variant">
      <Icon v-if="badgeInfo.icon" :name="badgeInfo.icon" class="mr-1" size="small" />
      {{ badgeInfo.text }}
    </Badge>
  </template>
</template>

<style lang="scss" scoped>
/* Add any specific styles for the badge container if needed */
.mr-1 {
  margin-right: 0.25rem; // Equivalent to Tailwind mr-1
}
</style>
